import { ApiError } from "./api";

// Loads the overview data. Only the principal is required: every other source is optional, so an integration that is
// deliberately absent in an environment (Breaking Glass in InfraTeams Dev answers 503) or a failing GitHub read can
// never hide the signed-in user, the role or the notifications (ADO migration, sprint 2, Teams test).
export type Fetcher = <T>(path: string) => Promise<T>;

export type DashboardData<P, N, G, O, R, B, BG> = {
  principal: P;
  notifications: N[];
  gates: G[];
  onboardings: O[];
  repositories: R[];
  branchTips: B[];
  breakglass: BG | null;
  // Informational: integrations the server marks as deliberately not configured here (503 + code "not_configured").
  notConfigured: string[];
  // Real failures of optional sources; shown as error but without blocking the rest.
  failed: string[];
};

const OPTIONAL = {
  notifications: ["/api/notifications", "Benachrichtigungen"],
  gates: ["/api/github/gates", "GitHub-Freigaben"],
  onboardings: ["/api/github/onboarding", "Repository-Lifecycle"],
  repositories: ["/api/github/repositories", "Repository-Lifecycle"],
  branchTips: ["/api/github/protected-branches", "Repository-Lifecycle"],
  breakglass: ["/api/breakglass/overview", "Breaking Glass"],
} as const;

export async function loadDashboard<P, N, G, O, R, B, BG>(fetcher: Fetcher): Promise<DashboardData<P, N, G, O, R, B, BG>> {
  const keys = Object.keys(OPTIONAL) as Array<keyof typeof OPTIONAL>;
  const [principal, ...results] = await Promise.all([
    fetcher<P>("/api/me"),
    ...keys.map((key) => fetcher<unknown>(OPTIONAL[key][0]).then(
      (value) => ({ ok: true as const, value }),
      (error: unknown) => ({ ok: false as const, error }),
    )),
  ]);
  const notConfigured = new Set<string>();
  const failed = new Set<string>();
  const value = <T>(key: keyof typeof OPTIONAL): T | null => {
    const result = results[keys.indexOf(key)];
    if (result.ok) return result.value as T;
    // Only the explicit marker counts as "not configured"; any other 503 is a real outage and reported as failure.
    if (result.error instanceof ApiError && result.error.status === 503 && result.error.code === "not_configured") notConfigured.add(OPTIONAL[key][1]);
    else failed.add(OPTIONAL[key][1]);
    return null;
  };
  return {
    principal,
    notifications: value<N[]>("notifications") ?? [],
    gates: value<G[]>("gates") ?? [],
    onboardings: value<O[]>("onboardings") ?? [],
    repositories: value<R[]>("repositories") ?? [],
    branchTips: value<B[]>("branchTips") ?? [],
    breakglass: value<BG>("breakglass"),
    notConfigured: [...notConfigured],
    failed: [...failed],
  };
}
